/**
 * @flow
 */

import React, { useState } from 'react';
import { Modal, Header, Button, Icon, Message } from 'semantic-ui-react';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { DELETE_SEMINAR_QUERY, GET_SEMINARS_QUERY } from '../containers/queries';

import type { ModalDialog, Seminar } from '../../../type';
import { get } from 'lodash';

const MessageBlock = styled(Message)`
    background-color: transparent !important;
    border: 2px solid #ff4545;
    color: #ff4545 !important;
    font-weight: bold;
    box-shadow: none !important;
`;

type DeleteSeminarDialogProps = {
    seminar: Seminar,
} & ModalDialog;

const intlMessages = defineMessages({
    existingsError: {
        id: 'pages.seminars.form.SeminarForm.existingsError',
        defaultMessage: 'This seminar does not exist',
    },
    hasVisitorsError: {
        id: 'pages.seminars.form.SeminarForm.hasVisitorsError',
        defaultMessage: 'This seminar already has visitors attended',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

function DeleteSeminarDialog({ seminar, trigger, open, onClose, intl }: DeleteSeminarDialogProps) {
    const searchQuery = useSelector(({ seminars }) => seminars.get('searchQuery'));
    const [deleteFailed, setDeleteFailed] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [deleteSeminar, { loading }] = useMutation(DELETE_SEMINAR_QUERY, {
        update: (store) => {
            try {
                const variables = { search: searchQuery || undefined, first: 20 };
                const data = store.readQuery({ query: GET_SEMINARS_QUERY, variables });
                store.writeQuery({
                    query: GET_SEMINARS_QUERY,
                    variables,
                    data: {
                        ...data,
                        viewer: {
                            ...data.viewer,
                            seminars: {
                                ...data.viewer.seminars,
                                edges: data.viewer.seminars.edges.filter(edge => edge.node.id !== seminar.id),
                            },
                        },
                    },
                });
            } catch (e) {
                // cache not populated — safe to ignore
            }
        },
    });

    const onDelete = () => {
        setDeleteFailed(false);
        deleteSeminar({
            variables: { input: { seminarId: seminar.id } },
        }).then(() => {
            onClose();
        }).catch(error => {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            let msg = intl.formatMessage(intlMessages.defaultErrorMessage);
            if (graphQLError?.data) {
                if (graphQLError.name === 'NotExists' && get(graphQLError, 'data.error') === 'ID_DOES_NOT_EXISTS') {
                    msg = intl.formatMessage(intlMessages.existingsError);
                } else if (get(graphQLError, 'data.error') === 'SEMINAR_HAS_VISITORS') {
                    msg = intl.formatMessage(intlMessages.hasVisitorsError);
                }
            }
            setDeleteFailed(true);
            setErrorMessage(msg);
        });
    };

    return (
        <Modal trigger={trigger} open={open} onClose={onClose} size="small" basic>
            <Header icon>
                <Icon name="remove" />
                <FormattedMessage
                    id="pages.seminars.DeleteSeminarDialog.deleteSeminar"
                    defaultMessage="Delete Seminar"
                />
            </Header>
            <Modal.Content>
                <FormattedMessage
                    id="pages.seminars.DeleteSeminarDialog.confirmMessage"
                    defaultMessage="Are you sure you want to delete this seminar?"
                >
                    {message => <p>{message}</p>}
                </FormattedMessage>
                {deleteFailed && (
                    <MessageBlock error>
                        <Icon name="warning sign" />
                        {errorMessage}
                    </MessageBlock>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose} color="red" basic inverted>
                    <Icon name="remove" />
                    <FormattedMessage
                        id="pages.seminars.DeleteSeminarDialog.no"
                        defaultMessage="No"
                    />
                </Button>
                <Button
                    onClick={onDelete}
                    disabled={loading}
                    loading={loading}
                    color="green"
                    inverted
                >
                    <Icon name="checkmark" />
                    <FormattedMessage
                        id="pages.seminars.DeleteSeminarDialog.yes"
                        defaultMessage="Yes"
                    />
                </Button>
            </Modal.Actions>
        </Modal>
    );
}

export default injectIntl(DeleteSeminarDialog);
